import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApp } from '@/contexts/AppContext';
import { Share2, Users, Coffee, Utensils, Truck, RefreshCw, RotateCw, ShoppingCart, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { API_CONFIG } from '@/config/apiConfig';
import { apiService } from '@/services/apiService';
import { testApiConnection } from '@/services/orderApiService';
import { checkPrintService } from '@/services/printService';

const Index = () => {
  const { currentUser, orders, synchronizeOrder } = useApp();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncAttempted, setSyncAttempted] = useState(false);
  const [printServiceAvailable, setPrintServiceAvailable] = useState<boolean | null>(null);
  
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.isManager === true;
  const isAdminOrManager = isAdmin || isManager;
  
  const empresaTipo = localStorage.getItem('empresa_tipo') || 'Restaurante';
  const isRestaurante = empresaTipo === 'Restaurante';
  
  console.log("Index - Verificação de permissões:", {
    currentUser,
    isAdmin,
    isManager,
    isAdminOrManager,
    empresaTipo,
    isRestaurante
  });
  
  const testApiConfiguration = async (): Promise<boolean> => {
    try {
      if (!API_CONFIG.baseURL) {
        API_CONFIG.initialize();
        await apiService.initialize();
      }

      return !!API_CONFIG.getBaseURL();
    } catch (error) {
      console.error('Error initializing API:', error);
      return false;
    }
  };
  
  const testApiConnectionHandler = async () => {
    const apiConfigured = await testApiConfiguration();
    if (apiConfigured) {
      toast({
        title: "Conexão estabelecida",
        description: "A API está configurada corretamente.",
      });
    } else {
      toast({
        title: "Falha na conexão",
        description: "Não foi possível conectar à API. Verifique as configurações.",
        variant: "destructive",
      });
    }
  };
  
  useEffect(() => {
    const checkApiConfig = async () => {
      if (syncAttempted) return;
      
      try {
        const apiConfigured = await testApiConfiguration();
        
        if (!apiConfigured && isAdminOrManager) {
          toast({
            title: "API não configurada",
            description: "Configure a API nas configurações para sincronizar pedidos",
            variant: "destructive",
            duration: 5000,
          });
        }
      } catch (error) {
        console.error("Erro ao verificar API:", error);
      } finally {
        setSyncAttempted(true);
      }
    };
    
    checkApiConfig();
  }, [isAdminOrManager, syncAttempted]);
  
  useEffect(() => {
    const checkPrinterServiceAvailability = async () => {
      try {
        const isAvailable = await checkPrintService();
        setPrintServiceAvailable(isAvailable);
      } catch (error) {
        console.error("Erro ao verificar serviço de impressão:", error);
        setPrintServiceAvailable(false);
      }
    };
    
    checkPrinterServiceAvailability();
  }, []);
  
  const goToSettings = () => {
    if (isAdminOrManager) {
      console.log("Navegando para /settings como admin ou gestor");
      navigate('/settings');
    }
  };

  const handleSyncPendingOrders = async (event?: React.MouseEvent): Promise<boolean> => {
    const showNotifications = !!event;
    
    if (isSyncing) {
      if (showNotifications) {
        toast({
          title: "Sincronização em andamento",
          description: "Aguarde a conclusão da sincronização atual",
          duration: 3000,
        });
      }
      return false;
    }
    
    setIsSyncing(true);
    
    try {
      const pendingOrders = orders.filter(order => 
        order.status === 'paid' && 
        (order.syncStatus === 'pending' || order.syncStatus === 'failed')
      );
      
      if (pendingOrders.length === 0) {
        if (showNotifications) {
          toast({
            title: "Nenhum pedido pendente",
            description: "Não há pedidos pendentes para sincronização",
            duration: 3000,
          });
        }
        return false;
      }

      if (showNotifications) {
        toast({
          title: 'Sincronizando pedidos',
          description: `Sincronizando ${pendingOrders.length} pedido(s) pendente(s)...`,
          duration: 3000,
        });
      }

      let successCount = 0;
      for (const order of pendingOrders) {
        const ok = await synchronizeOrder(order.id, true);
        if (ok) successCount++;
      }

      if (showNotifications) {
        if (successCount === pendingOrders.length) {
          toast({
            title: 'Sincronização concluída',
            description: `${successCount} de ${pendingOrders.length} pedidos sincronizados com sucesso.`,
            duration: 3000,
          });
        } else {
          toast({
            title: 'Sincronização finalizada com avisos',
            description: `${successCount} de ${pendingOrders.length} pedidos sincronizados. Verifique os que falharam.`,
            variant: 'destructive',
            duration: 4000,
          });
        }
      }

      return successCount === pendingOrders.length;
    } catch (error) {
      console.error("Erro ao sincronizar pedidos:", error);
      if (showNotifications) {
        toast({
          title: "Erro na sincronização",
          description: "Ocorreu um erro ao sincronizar os pedidos",
          variant: "destructive",
          duration: 3000,
        });
      }
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const showTables = (isRestaurante) && (isAdminOrManager || currentUser?.role === 'waiter');
  const showKitchen = (isRestaurante) && (isAdminOrManager || currentUser?.role === 'kitchen');
  const showDelivery = isAdminOrManager || currentUser?.role === 'delivery';
  const showPDV = isAdminOrManager || currentUser?.role === 'waiter';
  
  const pendingOrders = orders.filter(order => 
    order.status === 'paid' && 
    (order.syncStatus === 'pending' || order.syncStatus === 'failed')
  );
  const hasPendingOrders = pendingOrders.length > 0;
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-3xl font-bold">Bem-vindo ao RSP Cloud Mobile Web</h1>
      
      {hasPendingOrders && (
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {pendingOrders.length} {pendingOrders.length === 1 ? 'pedido pendente' : 'pedidos pendentes'} de sincronização
                </p>
                <p className="text-sm text-muted-foreground">
                  Sincronize os pedidos para atualizar o sistema ERP
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-yellow-400 hover:bg-yellow-100"
                onClick={handleSyncPendingOrders}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <>
                    <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RotateCw className="mr-2 h-4 w-4" />
                    Sincronizar Agora
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {showTables && (
          <Card 
            className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/tables')}
          >
            <CardHeader>
              <CardTitle className="flex items-center">
                <Utensils className="mr-2 h-5 w-5 text-amber-600" />
                Mesas e Pedidos
              </CardTitle>
              <CardDescription>Gerencie os pedidos do restaurante</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Acesse e gerencie todos os pedidos e mesas do restaurante.</p>
              <Button 
                onClick={() => navigate('/tables')} 
                className="mt-4 bg-amber-600 hover:bg-amber-700 w-full"
              >
                Ver mesas e pedidos →
              </Button>
            </CardContent>
          </Card>
        )}
        
        {showKitchen && (
          <Card 
            className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/kitchen')}
          >
            <CardHeader>
              <CardTitle className="flex items-center">
                <Coffee className="mr-2 h-5 w-5 text-green-600" />
                Cozinha
              </CardTitle>
              <CardDescription>Acompanhe os pedidos em preparo</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Visualize e atualize o status dos pedidos em preparo na cozinha.</p>
              <Button 
                onClick={() => navigate('/kitchen')} 
                className="mt-4 bg-green-600 hover:bg-green-700 w-full"
              >
                Ir para a cozinha →
              </Button>
            </CardContent>
          </Card>
        )}
        
        {showDelivery && (
          <Card 
            className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/delivery')}
          >
            <CardHeader>
              <CardTitle className="flex items-center">
                <Truck className="mr-2 h-5 w-5 text-blue-600" />
                Entregas
              </CardTitle>
              <CardDescription>Gerencie as entregas de pedidos</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Acompanhe e gerencie as entregas de pedidos aos clientes.</p>
              <Button 
                onClick={() => navigate('/delivery')} 
                className="mt-4 bg-blue-600 hover:bg-blue-700 w-full"
              >
                Ver entregas →
              </Button>
            </CardContent>
          </Card>
        )}
        
        {showPDV && (
          <Card 
            className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/pdv')}
          >
            <CardHeader>
              <CardTitle className="flex items-center">
                <ShoppingCart className="mr-2 h-5 w-5 text-purple-600" />
                PDV - Ponto de Venda
              </CardTitle>
              <CardDescription>Realize vendas rápidas para balcão</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Acesse o PDV para realizar vendas de balcão e atender clientes com rapidez.</p>
              <Button 
                onClick={() => navigate('/pdv')} 
                className="mt-4 bg-purple-600 hover:bg-purple-700 w-full"
              >
                Acessar PDV →
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      
      {isAdminOrManager && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">Configurações do Sistema</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center">
                  <Share2 className="h-5 w-5 mr-2 text-green-600" />
                  Sincronização
                </CardTitle>
                <CardDescription>
                  Sincronize dados com o ERP
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm">Gerencie a sincronização de produtos, pedidos e outras informações com o sistema ERP.</p>
                <Button 
                  onClick={() => navigate('/settings')} 
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Gerenciar Sincronização
                </Button>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 ${printServiceAvailable === false ? 'border-red-200' : 'border-cyan-200'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center">
                  <Printer className="h-5 w-5 mr-2 text-cyan-600" />
                  Impressão
                </CardTitle>
                <CardDescription>
                  Configure impressoras e relatórios
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm">Configure as impressoras para recibos, relatórios e documentos fiscais.</p>
                <div className="mb-4">
                  <div className={`text-xs flex items-center gap-1.5 ${
                    printServiceAvailable === null ? 'text-gray-500' : 
                    printServiceAvailable ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      printServiceAvailable === null ? 'bg-gray-400' : 
                      printServiceAvailable ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <span>
                      {printServiceAvailable === null ? 'Verificando serviço...' : 
                       printServiceAvailable ? 'Serviço de impressão conectado' : 
                       'Serviço de impressão não disponível'}
                    </span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/settings?tab=printer')} 
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                >
                  Configurar Impressoras
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
